import React from "react";
import {TicketTemplateFragmentFragment} from "../../../../__generated__/graphql/types";
import {VeriflytTicketTemplateSelectApi} from "./state";
import ETable from "../../../enchanced-antd-table";
import {ColumnProps} from "antd/lib/table";
import {Alert, Button, Col, Icon, Row, Spin, Typography} from "antd";
import {useGetTicketTemplateQuery} from "./graphql";
import {cleanGraphQLErrorMsg} from "../../../../common/util";
import {Cell, Column, SelectionModes, Table} from "@blueprintjs/table";

type SelectPanelOptions = {

    onTicketTemplateSelected?: (selectedTicketTemplateID: string, shortName: string) => void,

    /**
     * @default false
     */
    showCustomer?: boolean,

    disabledTicketTemplateIds?: string[],
    disabledMessage?: string,
    noticeAlertText?: string
}

export type SelectPanelProps = SelectPanelOptions & (
    {
        api: VeriflytTicketTemplateSelectApi
    } | {
        selectedTicketTemplateID?: string,
        ticketTemplateList: TicketTemplateFragmentFragment[]
    }
)

function getStateFromProps(props: SelectPanelProps){
    return {
        selectedTicketTemplateID: 'api' in props ? props.api.state.selectedTicketTemplateID : props.selectedTicketTemplateID,
    }
}

export const SelectPanel: React.FC<SelectPanelProps> = (props) => {
    const state = getStateFromProps(props)
    const { data: selectedTicketTemplate, loading, error } = useGetTicketTemplateQuery(state.selectedTicketTemplateID);

    function handleTicketTemplateSelected(ticketTemplateID: string, onScreenName: string){
        if (isDisabled(ticketTemplateID)) return;

        if (typeof props.onTicketTemplateSelected === 'function'){
            props.onTicketTemplateSelected(ticketTemplateID, onScreenName);
        }
    }

    function isSelected(id: string){
        return state.selectedTicketTemplateID === id
    }

    function isDisabled(id: string){
        if (!Array.isArray(props.disabledTicketTemplateIds)) return false;

        return props.disabledTicketTemplateIds.includes(id)
    }

    function getColumnTypoType(record: TicketTemplateFragmentFragment){
        return isSelected(record._id) || isDisabled(record._id) ? "secondary" : undefined as "secondary" | undefined
    }

    const columns: ColumnProps<TicketTemplateFragmentFragment>[] = [
        {
            key: 'onScreenName',
            title: 'Name',
            dataIndex: 'onScreenName',
            render: (text, record) => {
                return <Typography.Text type={getColumnTypoType(record)}>{text}</Typography.Text>
            },
            width: 130
        },
        {
            key: 'organization',
            title: 'Organization',
            width: 130,
            render: (_, record) => record.originatorDoc?.primaryName
        },
        {
            key: 'description',
            title: 'Desc',
            dataIndex: 'shortDescription',
            width: 140,
            render: (text, record) => {
                return <div style={{
                    maxWidth: '140px',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                }}>
                    <span
                        title={text}>
                        <Typography.Text type={getColumnTypoType(record)}>
                            {text}
                        </Typography.Text>
                    </span>
                </div>
            }
        }
    ]

    const ticketTemplateListColumns = [
        ...columns,
        {
            key: 'actions',
            label: '',
            render: (_, record: TicketTemplateFragmentFragment) => {

                if (isDisabled(record._id)){
                    return <Typography.Text type="warning">{props.disabledMessage}</Typography.Text>
                }

                if (record._id === state.selectedTicketTemplateID){
                    return null;
                }
                return <Row type="flex" justify="end">
                    <Col>
                        <Button type="link" size="small">Select</Button>
                    </Col>
                </Row>
            }
        }
    ]

    const ticketTemplateList = 'ticketTemplateList' in props ? props.ticketTemplateList : props.api.state.ticketTemplates;

    let selectedTicketTemplateContent = null;

    if (loading){
        selectedTicketTemplateContent = <Spin indicator={<Icon type="loading" />} />
    }

    if (!loading && selectedTicketTemplate){
        selectedTicketTemplateContent = (
            <ETable
                flexMode
                columns={columns}
                pagination={false}
                dataSource={[selectedTicketTemplate]}
                size="small"
                showHeader={false}
                bordered
                rowKey="_id"
            />
        )
    }

    let errorAlert: React.ReactNode = null;

    if (error){
        errorAlert = (
            <Alert
                type="error"
                showIcon
                message="An error occurred"
                description={cleanGraphQLErrorMsg(error)}
                style={{ marginBottom: 12 }}
            />
        )
    }

    return (
        <>
            {errorAlert}
            {props.noticeAlertText ? (
                <Alert
                    showIcon
                    message="Notice"
                    description={props.noticeAlertText}
                    type="info"
                    banner
                />
            ) : null}
            {selectedTicketTemplateContent ? (
                <div style={{
                    padding: 8
                }}>
                    <h4>Current Selection:</h4>
                    {selectedTicketTemplateContent}
                </div>
            ) : null}
            <Table
                className="mc-table"
                numRows={ticketTemplateList.length}
                defaultRowHeight={25}
                selectedRegions={[]}
                onSelection={(regions) => {
                    if (regions[0].cols[0] === 0) return;
                    let selectedRow = regions[0].rows[0];
                    const record = ticketTemplateList[selectedRow];
                    if (record) {
                        handleTicketTemplateSelected(record._id, record.onScreenName);
                    }
                }}
                selectionModes={SelectionModes.ROWS_AND_CELLS}
            >
                <Column name="Name" cellRenderer={(idx) => <Cell>{ticketTemplateList[idx].onScreenName}</Cell>} />
                <Column name="Organization" cellRenderer={(idx) => <Cell>{ticketTemplateList[idx].originatorDoc?.primaryName}</Cell>} />
                <Column name="Desc" cellRenderer={(idx) => <Cell>
                    <div style={{
                        maxWidth: '140px',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden'
                    }}>
                        <span title={ticketTemplateList[idx].shortDescription}>
                            {ticketTemplateList[idx].shortDescription}
                        </span>
                    </div>
                </Cell>} />
                <Column name="Actions" cellRenderer={(idx) => {
                    const record = ticketTemplateList[idx];
                    if (isDisabled(record._id)){
                        return <Cell>
                            <Typography.Text type="warning">{props.disabledMessage}</Typography.Text>
                        </Cell>
                    }

                    if (record._id === state.selectedTicketTemplateID){
                        return <Cell></Cell>;
                    }

                    return <Cell>
                        <Row type="flex" justify="center">
                            <Col>
                                <Button type="link" size="small" onClick={() => handleTicketTemplateSelected(record._id, record.onScreenName)}>Select</Button>
                            </Col>
                        </Row>
                    </Cell>
                }} />
            </Table>
        </>
    )
}

export default SelectPanel;